import { useRef } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ImagePlus, Plus, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatError, imgUrl } from "@/lib/api";

const ICONS = ["reading", "wellness", "shea", "fabric", "gift", "star"];

const PrizeRow = ({ p, i, total, update, move, remove }) => {
  const fileRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("kind", "prize");
      fd.append("caption", p.name);
      fd.append("file", file);
      const { data } = await api.post("/admin/images", fd);
      update({ image_id: data.id });
      toast.success("Image added — remember to Save content");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      e.target.value = "";
    }
  };
  return (
    <div className="flex gap-3 rounded-md border border-neutral-800 p-3" data-testid={`prize-row-${i}`}>
      <div className="flex shrink-0 flex-col items-center gap-1">
        <button type="button" onClick={() => fileRef.current?.click()} className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border border-dashed border-neutral-700 text-neutral-500 hover:border-amber-500" data-testid={`prize-${i}-image-button`} title="Upload image">
          {p.image_id ? <img src={imgUrl(`/api/images/${p.image_id}`)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5" />}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`prize-${i}-image-input`} />
        {p.image_id && (
          <button type="button" onClick={() => update({ image_id: null })} className="flex items-center text-[11px] text-neutral-500 hover:text-red-400" data-testid={`prize-${i}-image-remove`}>
            <X className="h-3 w-3" /> image
          </button>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex gap-2">
          <Input value={p.name} onChange={(e) => update({ name: e.target.value })} placeholder="Prize name" className="h-9 border-neutral-800 bg-neutral-950" data-testid={`prize-${i}-name`} />
          <select value={p.icon} onChange={(e) => update({ icon: e.target.value })} className="h-9 rounded-md border border-neutral-800 bg-neutral-950 px-2 text-sm" data-testid={`prize-${i}-icon`} title="Icon (used when no image)">
            {ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
          </select>
        </div>
        <Input value={p.description} onChange={(e) => update({ description: e.target.value })} placeholder="Short description" className="h-9 border-neutral-800 bg-neutral-950" data-testid={`prize-${i}-description`} />
        <div className="flex items-center gap-1">
          <button type="button" disabled={i === 0} onClick={() => move(-1)} className="rounded p-1.5 text-neutral-400 hover:bg-neutral-800 disabled:opacity-30" data-testid={`prize-${i}-up`}><ArrowUp className="h-4 w-4" /></button>
          <button type="button" disabled={i === total - 1} onClick={() => move(1)} className="rounded p-1.5 text-neutral-400 hover:bg-neutral-800 disabled:opacity-30" data-testid={`prize-${i}-down`}><ArrowDown className="h-4 w-4" /></button>
          <button type="button" onClick={remove} className="ml-auto rounded p-1.5 text-neutral-500 hover:text-red-400" data-testid={`prize-${i}-remove`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export const PrizesEditor = ({ prizes, onChange }) => {
  const list = prizes || [];
  const update = (i) => (patch) => onChange(list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const move = (i) => (dir) => {
    const next = [...list];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="prizes-editor">
      {list.map((p, i) => (
        <PrizeRow key={i} p={p} i={i} total={list.length} update={update(i)} move={move(i)} remove={() => onChange(list.filter((_, j) => j !== i))} />
      ))}
      <button type="button" onClick={() => onChange([...list, { name: "New prize", description: "", icon: "gift", image_id: null }])} className="flex items-center gap-2 text-sm font-semibold text-amber-400" data-testid="prize-add">
        <Plus className="h-4 w-4" /> Add prize
      </button>
    </div>
  );
};
